"use client";

import { trpcApi } from "@lattice/frontend/lib/trpc/client/index";
import type { T_AnalystWidgetSpecTypes } from "@lattice/core-lib/types/widget/widget.types";
import { Progress } from "../ui/progress";
import { camelToTitleCase } from "@lattice/frontend/lib/utils/text-transform";
import { format } from "date-fns";
import React from "react";
import PriceTargetChart from "./price-target-chart";
import { cn, gothamFonts } from "@lattice/frontend/lib/utils/ui";
import { cardPadding } from "@lattice/frontend/lib/styles/common";

export default function AnalystRecommendations({
  spec,
}: {
  spec: T_AnalystWidgetSpecTypes;
}) {
  const data =
    trpcApi.stock.get_analyst_recommendations.useSuspenseQuery(spec)[0];

  const valid_keys = [
    "analystRatingsStrongBuy",
    "analystRatingsBuy",
    "analystRatingsHold",
    "analystRatingsSell",
    "analystRatingsStrongSell",
  ] as const;

  const max = React.useMemo(() => {
    if (!data) return "analystRatingsStrongBuy";
    return valid_keys.reduce((prev, curr) => {
      return data.grade[curr] > data.grade[prev] ? curr : prev;
    }, "analystRatingsStrongBuy");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- valid_keys is a constant array defined outside the component; including it would be misleading
  }, [data]);

  const getMaxColorClasses = React.useMemo(() => {
    if (max.includes("Buy")) {
      return {
        bg: "bg-green-500/10",
        indicator: "bg-green-500",
        border: "border-green-500/20",
        color: "#10b981",
      };
    }
    if (max === "analystRatingsHold") {
      return {
        bg: "bg-yellow-500/10",
        indicator: "bg-yellow-500",
        border: "border-yellow-500/20",
        color: "#eab308",
      };
    }
    if (max.includes("Sell")) {
      return {
        bg: "bg-red-500/10",
        indicator: "bg-red-500",
        border: "border-red-500/20",
        color: "#ef4444",
      };
    }
    return {
      bg: "bg-green-500/10",
      indicator: "bg-green-500",
      border: "border-green-500/20",
      color: "#10b981",
    };
  }, [max]);

  if (!data)
    return (
      <div className="w-full h-full flex items-center justify-center p-8 text-muted-foreground">
        No analyst data available
      </div>
    );

  const total =
    data.grade.analystRatingsBuy +
    data.grade.analystRatingsHold +
    data.grade.analystRatingsSell +
    data.grade.analystRatingsStrongBuy +
    data.grade.analystRatingsStrongSell;

  return (
    <div className="w-full space-y-6">
      <div
        className={cn("w-full bg-grey-7 rounded-lg", cardPadding)}
        style={{
          borderWidth: "1px",
          borderColor: "#131314",
        }}
      >
        <div className="mb-6">
          <p className="font-semibold text-lg sm:text-xl text-white/90">
            {total} Analyst Ratings
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 sm:gap-10 w-full">
          <div
            className="rounded-full w-28 h-28 sm:w-32 sm:h-32 flex items-center justify-center shrink-0 backdrop-blur-sm shadow-sm"
            style={{
              backgroundColor: `${getMaxColorClasses.color}15`,
              borderWidth: "2px",
              borderColor: `${getMaxColorClasses.color}50`,
            }}
          >
            <p
              className="max-w-full min-w-0 whitespace-normal text-center text-base sm:text-lg font-semibold px-2 uppercase"
              style={{ color: getMaxColorClasses.color }}
            >
              {camelToTitleCase(max.replace("analystRatings", ""))}
            </p>
          </div>
          <div className="flex flex-col gap-3 w-full">
            <table className="w-full table-auto">
              <tbody>
                {valid_keys.map((key) => {
                  const isMax = max === key;
                  return (
                    <tr key={key}>
                      <td className="w-full pb-2">
                        <Progress
                          className="bg-foreground/20"
                          indicatorClassName={
                            isMax ? "" : "dark:bg-white/85 bg-gray-400"
                          }
                          indicatorStyle={
                            isMax
                              ? {
                                  background: `${getMaxColorClasses.color}CC`,
                                }
                              : undefined
                          }
                          value={Math.round(
                            (Number(data.grade[key]) / total) * 100,
                          )}
                        />
                      </td>
                      <td className="pl-3 sm:pl-5 min-w-fit whitespace-nowrap text-right text-xs sm:text-sm pb-2">
                        {Math.round((Number(data.grade[key]) / total) * 100)}%{" "}
                        {camelToTitleCase(key.replace("analystRatings", ""))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <div className="mt-6 flex justify-end">
          <p
            className="text-[10px] font-normal text-muted-foreground/60 italic"
            style={gothamFonts.sidebarSubheader}
          >
            As of {format(new Date(data.grade.date), "MMM dd, yyyy")}
          </p>
        </div>
      </div>
      <div
        className={cn("w-full bg-grey-7 rounded-lg", cardPadding)}
        style={{
          borderWidth: "1px",
          borderColor: "#131314",
        }}
      >
        <div className="mb-6">
          <p className="font-semibold text-lg sm:text-xl text-white/90">
            Price Target
          </p>
        </div>
        <PriceTargetChart
          currentPrice={data.price_target.currentPrice}
          targetLow={data.price_target.targetLow}
          targetMedian={data.price_target.targetMedian}
          targetHigh={data.price_target.targetHigh}
        />
      </div>
    </div>
  );
}
